import { ONBOARDING_FEATURED_TEMPLATES } from "@/constants/onboardingFeaturedTemplates";
import { bundledOnboardingFeaturedTemplate } from "@/data/onboardingFeaturedTemplateSnapshots";
import {
  fetchTripTemplates,
  filterTemplates,
  getTemplateById,
} from "@/services/tripTemplates";
import {
  mergeTemplateCatalog,
  pickBestTemplateForFeaturedRef,
  templateHasTopicTag,
  templateMatchesFeaturedRef,
} from "@/services/onboardingFeaturedTemplateMatching";
import type { OnboardingFeaturedTemplateRef } from "@/constants/onboardingFeaturedTemplates";
import type { TripTemplate } from "@/types/tripTemplates";

const FEATURED_SLOT_COUNT = ONBOARDING_FEATURED_TEMPLATES.length;

export type OnboardingFeaturedTemplatesResult = {
  templates: TripTemplate[];
  /** How many slots resolved to a live Firestore document (not bundled snapshot). */
  firestoreBackedCount: number;
};

async function loadApprovedCatalog(useCache: boolean): Promise<TripTemplate[]> {
  const fetched = await fetchTripTemplates(useCache, { skipImageBackfill: true });
  const approved = filterTemplates(fetched, { status: ["approved"] });
  return approved.length > 0 ? approved : fetched;
}

async function resolveFeaturedSlotFromFirestore(
  catalog: TripTemplate[],
  ref: OnboardingFeaturedTemplateRef,
  excludeIds: Set<string>,
): Promise<TripTemplate | null> {
  const fromCatalog = pickBestTemplateForFeaturedRef(catalog, ref, excludeIds);
  if (fromCatalog) {
    return fromCatalog;
  }

  const byId = await getTemplateById(ref.id).catch(() => null);
  if (byId && !excludeIds.has(byId.id)) {
    return byId;
  }

  return null;
}

export async function resolveOnboardingTemplateId(
  ref: OnboardingFeaturedTemplateRef,
): Promise<string> {
  let catalog: TripTemplate[] = [];

  try {
    catalog = await loadApprovedCatalog(false);
  } catch (error) {
    console.warn("[OnboardingFeaturedTemplates] resolve template catalog failed", error);
  }

  const match = await resolveFeaturedSlotFromFirestore(catalog, ref, new Set());
  if (match) {
    return match.id;
  }

  throw new Error(
    "This itinerary template is not available in your environment yet. Try again later or skip for now.",
  );
}

export async function loadOnboardingFeaturedTemplates(options?: {
  pendingTemplateId?: string;
  pendingTopicTag?: string;
}): Promise<OnboardingFeaturedTemplatesResult> {
  let catalog: TripTemplate[] = [];

  try {
    catalog = await loadApprovedCatalog(true);
  } catch (error) {
    console.warn("[OnboardingFeaturedTemplates] fetchTripTemplates failed", error);
  }

  if (options?.pendingTemplateId) {
    const pendingFromCatalog = catalog.find((template) => template.id === options.pendingTemplateId);
    if (!pendingFromCatalog) {
      try {
        const pending = await getTemplateById(options.pendingTemplateId);
        if (pending) {
          catalog = mergeTemplateCatalog(catalog, [pending]);
        }
      } catch {
        // Ignore — pending deeplink template may not exist in this Firebase project.
      }
    }
  }

  const resolveSlots = async (sourceCatalog: TripTemplate[]) => {
    const slots: TripTemplate[] = [];
    const seen = new Set<string>();
    let firestoreBackedCount = 0;

    for (const ref of ONBOARDING_FEATURED_TEMPLATES) {
      const live = await resolveFeaturedSlotFromFirestore(sourceCatalog, ref, seen);
      const template = live ?? bundledOnboardingFeaturedTemplate(ref);
      if (live) {
        firestoreBackedCount += 1;
      }
      seen.add(template.id);
      slots.push(template);
    }

    return { slots, firestoreBackedCount };
  };

  let { slots, firestoreBackedCount } = await resolveSlots(catalog);

  if (firestoreBackedCount < FEATURED_SLOT_COUNT) {
    try {
      const freshCatalog = mergeTemplateCatalog(catalog, await loadApprovedCatalog(false));
      const refreshed = await resolveSlots(freshCatalog);
      if (refreshed.firestoreBackedCount > firestoreBackedCount) {
        slots = refreshed.slots;
        firestoreBackedCount = refreshed.firestoreBackedCount;
        catalog = freshCatalog;
      }
    } catch (error) {
      console.warn("[OnboardingFeaturedTemplates] fresh template fetch failed", error);
    }
  }

  if (options?.pendingTemplateId) {
    const pendingRef = ONBOARDING_FEATURED_TEMPLATES.find((ref) => ref.id === options.pendingTemplateId);
    const pendingTemplate =
      catalog.find((template) => template.id === options.pendingTemplateId) ??
      slots.find((template) => template.id === options.pendingTemplateId);

    if (pendingTemplate && pendingRef) {
      const index = ONBOARDING_FEATURED_TEMPLATES.findIndex((ref) => ref.id === pendingRef.id);
      if (index >= 0) {
        slots[index] = pendingTemplate;
      }
    } else if (options.pendingTopicTag) {
      const topicMatch = catalog.find((template) =>
        templateHasTopicTag(template, options.pendingTopicTag!),
      );
      if (topicMatch) {
        const refIndex = ONBOARDING_FEATURED_TEMPLATES.findIndex((ref) =>
          templateMatchesFeaturedRef(topicMatch, ref),
        );
        if (refIndex >= 0) {
          slots[refIndex] = topicMatch;
        }
      }
    }
  }

  if (firestoreBackedCount < FEATURED_SLOT_COUNT) {
    console.warn("[OnboardingFeaturedTemplates] Using bundled snapshots for missing slots", {
      firestoreBackedCount,
      expectedCount: FEATURED_SLOT_COUNT,
    });
  }

  return { templates: slots, firestoreBackedCount };
}
